import { useEffect, useRef, useState } from 'react';
import { useInView, useScroll, useTransform, MotionValue } from 'framer-motion';

/**
 * Hook for fade-in and slide-up animation when element enters viewport
 */
export const useScrollReveal = (threshold: number = 0.2) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: threshold });

  return { ref, isInView };
};

/**
 * Hook for animated counter that counts up when in view
 */
export const useCounter = (end: number, duration: number = 2000) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (!isInView) return;

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth counting
      const easeOut = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(easeOut * end));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => {
      if (animationFrame) cancelAnimationFrame(animationFrame);
    };
  }, [isInView, end, duration]);

  return { ref, count };
};

/**
 * Hook for parallax effect based on scroll position
 */
export const useParallax = (offset: number = 50) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start']
  });

  const y = useTransform(scrollYProgress, [0, 1], [-offset, offset]);

  return { ref, y };
};

/**
 * Hook for scroll progress (useful for sticky sections)
 */
export const useScrollProgress = () => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end']
  });

  return { ref, scrollYProgress };
};

/**
 * Hook for detecting when element is in viewport with configurable threshold
 */
export const useInViewport = (threshold: number = 0.3) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { amount: threshold });

  return { ref, isInView };
};
